import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { useSyncSchedule } from '../../api/sync-bar/use-sync-schedule';
import { getLastSyncedText } from '../../utils/utilities';

// These styles allow the sync bar to be placed outside a SettingsSection while maintaining the same look as if it were inside, by removing the default padding and background of the SettingsSection and applying our own styles.
export const settingsSectionSyncBarStyle = css({
  background: theme.colors.white,
  padding: theme.spacing(3),
  borderRadius: `0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium}`,
  boxShadow: theme.shadows.light,
});

type SyncBarProps = {
  locationId: string;
  lastSyncedAt: Date | null;
  onPublishSuccess: () => void;
};

/**
 * Sync bar used on scheduling proxy pages (Availability Rules,
 * Service Types, etc.) that need to push their state into TrueLark
 * via the sync API.
 */
export const SyncBar = ({ locationId, lastSyncedAt, onPublishSuccess }: SyncBarProps) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { selectedOrgId } = useAppScopeStore();

  const { syncSchedule, isSyncing } = useSyncSchedule({
    orgId: selectedOrgId,
    locationId,
    onSuccess: onPublishSuccess,
  });

  const isLastSyncedAtValid = lastSyncedAt !== null && !isNaN(lastSyncedAt.getTime());

  const lastSyncedText = (
    <span css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginRight: theme.spacing(3) }}>
      <Icon name='check-small' color='success' />
      <Text weight='bold'>{getLastSyncedText(lastSyncedAt)}</Text>
    </span>
  );

  return (
    <div css={containerStyle}>
      {isLastSyncedAtValid && lastSyncedText && (
        <Text size='small' color='subdued'>
          {lastSyncedText}
        </Text>
      )}
      <Button size='small' variant='tertiary' iconName='update' onClick={syncSchedule} disabled={isSyncing}>
        {t('Publish')}
      </Button>
    </div>
  );
};

const containerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(1.5),
  borderRadius: `${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0`,
  backgroundColor: theme.colors.neutral5,
  marginTop: theme.spacing(1),
  boxShadow: theme.shadows.light,
});
